import InputEAN, { PREFIX } from '../forms/InputEan';
import {
  MeterOnboardingStateProps,
  meterOnboardingState,
} from '../../atoms/meterOnboardingWizardAtom';
import { UIStateProps, uiState } from '../../atoms/uiAtom';
import { useEffect, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import Button from '../common/Button';
import { EanEligibilityStatusEnum } from '../../enum/EanEligibilityStatusEnum';
import { EanUtils } from '../../common/eanUtils';
import { Link } from 'react-router-dom';
import Loader from '../common/Loader';
import ModalInformation from '../modals/ModalInformation';
import { STEP_ASK_CONTENT_METER_ID } from './StepAskConsentMeter';
import { STEP_NO_ACCESS_METER_ID } from './StepNoAcccessMeter';
import { routes } from '../../routes';
import { useForm } from 'react-hook-form';
import { useGetEanValidate } from '../../hooks/meters';
import { useGetIsAccMember } from '../../hooks/sdps';
import { useTranslation } from 'react-i18next';
import { useWizard } from 'react-use-wizard';

export const STEP_ADD_EAN_ID = 'step-add-meter';

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: unknown }).status;
    if (typeof status === 'number') {
      return status;
    }
  }

  return undefined;
};

export default function StepAddMeter() {
  const { t } = useTranslation();
  const [isOpenModalEanInformation, setIsOpenModalEanInformation] = useState(false);
  const [ean, setEan] = useState<string | null | undefined>();
  const { isFetching: isFetchingEanValidate, refetch: fetchEanValidate } = useGetEanValidate(ean);

  const { isFetching: isFetchingIsAccMember, refetch: fetchIsAccMember } = useGetIsAccMember(ean);

  const {
    register,
    control,
    setError,
    formState: { errors },
    handleSubmit,
  } = useForm();
  const { nextStep, goToStep } = useWizard();
  const setUiState = useSetRecoilState<UIStateProps>(uiState);
  const [meterOnboarding, setMeterOnboardingState] =
    useRecoilState<MeterOnboardingStateProps>(meterOnboardingState);

  useEffect(() => {
    if (ean) {
      fetchEanValidate()
        .then((eanEligibilityOutResponse) => {
          if (eanEligibilityOutResponse.error) {
            const errorStatus = getErrorStatus(eanEligibilityOutResponse.error);

            if (errorStatus === 417) {
              setError('ean', {
                type: 'manual',
                message: t(
                  EanUtils.getEanEligibilityStatusError(EanEligibilityStatusEnum.DeleteRequest)
                ),
              });
              setEan(null);
            } else if (errorStatus === 409) {
              setError('ean', {
                type: 'manual',
                message: t(
                  EanUtils.getEanEligibilityStatusError(EanEligibilityStatusEnum.ConflictRequest)
                ),
              });
              setEan(null);
            } else {
              setUiState((prev) => ({ ...prev, hasError: true }));
              setEan(null);
            }
          } else if (EanUtils.isEligibilityError(eanEligibilityOutResponse.data?.status)) {
            setError('ean', {
              type: 'manual',
              message: t(
                EanUtils.getEanEligibilityStatusError(
                  eanEligibilityOutResponse.data!.status as number
                )
              ),
            });
          } else {
            fetchIsAccMember()
              .then((isAccMemberResponse) => {
                if (isAccMemberResponse.error) {
                  setUiState((prev) => ({ ...prev, hasError: true }));
                  setEan(null);
                } else {
                  setUiState((prev) => ({ ...prev, hasError: false }));
                  const eligibilityStatus = EanUtils.getEanEligibilityStatus(
                    eanEligibilityOutResponse.data!.data!.code
                  );
                  switch (eligibilityStatus) {
                    case EanEligibilityStatusEnum.Success:
                      setMeterOnboardingState((state) => ({
                        ...state,
                        ean,
                        isAccMember: isAccMemberResponse?.data?.status === 200,
                        eanEligibilityOutResponse: eanEligibilityOutResponse.data?.data!,
                      }));
                      nextStep();
                      break;
                    case EanEligibilityStatusEnum.AskConsent:
                      setMeterOnboardingState((state) => ({
                        ...state,
                        ean,
                        eanEligibilityOutResponse: eanEligibilityOutResponse.data?.data!,
                      }));
                      goToStep(
                        meterOnboarding.steps?.findIndex((s) => s.id === STEP_ASK_CONTENT_METER_ID)!
                      );
                      break;

                    case EanEligibilityStatusEnum.ContractNotRegisteredWithEmail:
                      setMeterOnboardingState((state) => ({
                        ...state,
                        ean,
                        eanEligibilityOutResponse: eanEligibilityOutResponse.data?.data!,
                      }));
                      goToStep(
                        meterOnboarding.steps?.findIndex((s) => s.id === STEP_NO_ACCESS_METER_ID)!
                      );
                      break;
                    case EanEligibilityStatusEnum.Invalid:
                    case EanEligibilityStatusEnum.ContractNotFound:
                    case EanEligibilityStatusEnum.NotYearlyMetering:
                    case EanEligibilityStatusEnum.NoActiveDevice:
                    case EanEligibilityStatusEnum.ConflictRequest:
                      setError('ean', {
                        type: 'manual',
                        message: t(EanUtils.getEanEligibilityStatusError(eligibilityStatus)),
                      });
                      setEan(null);
                      break;
                    default:
                      setUiState((prev) => ({
                        ...prev,
                        hasError: t(EanUtils.getEanEligibilityStatusError(eligibilityStatus)),
                      }));
                      setEan(null);
                  }
                }
              })
              .catch(() => {
                setUiState((prev) => ({ ...prev, hasError: true }));
                setEan(null);
              });
          }
        })
        .catch(() => {
          setUiState((prev) => ({ ...prev, hasError: true }));
          setEan(null);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ean, meterOnboarding.steps]);

  const onSubmit = async (data: { ean?: unknown }) => {
    if (typeof data.ean === 'string') {
      // Remove all non-digit characters from the EAN and set it in the state
      setEan(`${PREFIX}${data.ean}`.replace(/\D/g, ''));
    }
  };

  const handleClickEanInfo = () => {
    setIsOpenModalEanInformation(true);
  };

  const handleClickActionEanInfo = () => {
    setIsOpenModalEanInformation(false);
  };

  return (
    <div className="wizard-step step-add-meter" data-test-id={STEP_ADD_EAN_ID}>
      <form className="form" onSubmit={handleSubmit(onSubmit)} data-test-id="add-meter-form">
        <div className="form__container">
          <InputEAN
            dataTestId="input-ean"
            control={control}
            register={register}
            onClickInfo={handleClickEanInfo}
            error={errors.ean?.message as string}
          />
          <div className="step-add-meter__caption">
            {t('step.add.meter.caption.first')}
            <strong>{t('step.add.meter.caption.second')}</strong>
            {t('step.add.meter.caption.third')}
            <a href={t('step.add.meter.caption.link.uri')} target="_blank" rel="noreferrer">
              {t('step.add.meter.caption.link')}
            </a>
            &nbsp;
            {t('step.add.meter.caption.last')}
          </div>
        </div>

        <div className="wizard__actions">
          <Link
            to={routes.OVERVIEW.path}
            className="btn btn--secondary"
            data-test-id="add-meter-back-to-overview-btn"
          >
            {t('common.cancel')}
          </Link>
          <Button
            isDisabled={isFetchingEanValidate || isFetchingIsAccMember || !!errors.ean?.message}
            prefix={isFetchingEanValidate || isFetchingIsAccMember ? <Loader /> : null}
            type="submit"
            className="btn btn--primary"
            dataTestId="add-meter-submit-btn"
          >
            {t('common.next')}
          </Button>
        </div>
      </form>
      <ModalInformation isOpen={isOpenModalEanInformation} onClickAction={handleClickActionEanInfo}>
        <h2>{t('step.add.meter.ean.information.card.first')}</h2>
        <p>{t('step.add.meter.ean.information.card.second')}</p>
        <p>{t('step.add.meter.ean.information.card.third')}</p>
      </ModalInformation>
    </div>
  );
}
